import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { EmailCredential } from "@/actions/ssr/settings/getEmailCredentialsSSR";

/**
 * Email Credentials State Interface
 * Manages state for email credential operations including CRUD and testing
 */
export interface EmailCredentialsState {
  emailCredentials: EmailCredential[];
  emailCredentialsLoading: boolean;
  selectedEmailCredential: EmailCredential | null;

  // Create/Edit dialog state
  emailCredentialCreateEditDialogOpen: {
    open: boolean;
    openFor: "create" | "edit" | "view";
    editItemData?: Partial<EmailCredential>;
  };

  emailCredentialCreateEditLoading: boolean;
  emailCredentialCreateEditError: string | null;
  emailCredentialCreateEditFieldErrors: Record<string, string[]> | null;

  emailCredentialDeleteLoading?: {
    loading: boolean;
    deleteItemId?: string;
  };

  isEmailCredentialDialogOpen: {
    open: boolean;
    deleteItemId: string | undefined;
  };

  // Test email loading state
  emailCredentialTestLoading: boolean;

  // Activate credential loading state
  emailCredentialActivateLoading: boolean;
}

const initialState: EmailCredentialsState = {
  emailCredentials: [],
  emailCredentialsLoading: false,
  selectedEmailCredential: null,

  emailCredentialCreateEditDialogOpen: {
    open: false,
    openFor: "create",
    editItemData: undefined,
  },

  emailCredentialCreateEditLoading: false,
  emailCredentialCreateEditError: null,
  emailCredentialCreateEditFieldErrors: null,

  emailCredentialDeleteLoading: {
    loading: false,
    deleteItemId: undefined,
  },

  isEmailCredentialDialogOpen: {
    open: false,
    deleteItemId: undefined,
  },

  emailCredentialTestLoading: false,
  emailCredentialActivateLoading: false,
};

export const emailCredentialsSlice = createSlice({
  name: "emailCredentials",
  initialState,
  reducers: {
    setEmailCredentials: (state, action: PayloadAction<EmailCredential[]>) => {
      state.emailCredentials = action.payload;
    },
    setEmailCredentialsLoading: (state, action: PayloadAction<boolean>) => {
      state.emailCredentialsLoading = action.payload;
    },
    setSelectedEmailCredential: (
      state,
      action: PayloadAction<EmailCredential | null>,
    ) => {
      state.selectedEmailCredential = action.payload;
    },
    setEmailCredentialCreateEditDialogOpen: (
      state,
      action: PayloadAction<{
        open: boolean;
        openFor: "create" | "edit" | "view";
        editItemData?: Partial<EmailCredential>;
      }>,
    ) => {
      state.emailCredentialCreateEditDialogOpen = action.payload;
    },
    clearEmailCredentialCreateEditDialogOpen: (state) => {
      state.emailCredentialCreateEditDialogOpen = {
        open: false,
        openFor: "create",
        editItemData: undefined,
      };
    },
    setEmailCredentialCreateEditLoading: (
      state,
      action: PayloadAction<boolean>,
    ) => {
      state.emailCredentialCreateEditLoading = action.payload;
    },
    setEmailCredentialCreateEditError: (
      state,
      action: PayloadAction<string | null>,
    ) => {
      state.emailCredentialCreateEditError = action.payload;
    },
    setEmailCredentialCreateEditFieldErrors: (
      state,
      action: PayloadAction<Record<string, string[]> | null>,
    ) => {
      state.emailCredentialCreateEditFieldErrors = action.payload;
    },
    setEmailCredentialDeleteLoading: (
      state,
      action: PayloadAction<{
        loading: boolean;
        deleteItemId?: string;
      }>,
    ) => {
      state.emailCredentialDeleteLoading = action.payload;
    },
    setIsEmailCredentialDialogOpen: (
      state,
      action: PayloadAction<{
        open: boolean;
        deleteItemId: string | undefined;
      }>,
    ) => {
      state.isEmailCredentialDialogOpen = action.payload;
    },
    /**
     * Set loading state for test email operation
     */
    setEmailCredentialTestLoading: (state, action: PayloadAction<boolean>) => {
      state.emailCredentialTestLoading = action.payload;
    },
    /**
     * Set loading state for activate credential operation
     */
    setEmailCredentialActivateLoading: (
      state,
      action: PayloadAction<boolean>,
    ) => {
      state.emailCredentialActivateLoading = action.payload;
    },
  },
});

export const {
  setEmailCredentials,
  setEmailCredentialsLoading,
  setSelectedEmailCredential,
  setEmailCredentialCreateEditDialogOpen,
  clearEmailCredentialCreateEditDialogOpen,
  setEmailCredentialCreateEditLoading,
  setEmailCredentialCreateEditError,
  setEmailCredentialCreateEditFieldErrors,
  setEmailCredentialDeleteLoading,
  setIsEmailCredentialDialogOpen,
  setEmailCredentialTestLoading,
  setEmailCredentialActivateLoading,
} = emailCredentialsSlice.actions;

export default emailCredentialsSlice.reducer;
